import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

import { getNewsflashes, Newsflash, postNewsflashAction } from '../../api/newsflashService'
import { keepPosition, SEVERITY_LABEL, SEVERITY_TAG, untilLabel } from './newsflash'

/** Checked on open, every five minutes, and when the tab becomes visible again. */
const POLL_MS = 5 * 60 * 1000

/**
 * Head-office newsflashes: a banner above the chat, most urgent first, and the
 * details in a pop-up. The same design as the Production and Order
 * Recommenders. A newsflash that must be confirmed has no close button; it goes
 * once the person opens the details and taps "Got it".
 *
 * The banner is fixed under the top bar (or beside the sidebar) and publishes
 * its height as `--newsflash-height`, which the chat area pads by.
 */
const NewsflashBanner: React.FC = () => {
  const [newsflashes, setNewsflashes] = useState<Newsflash[]>([])
  const [index, setIndex] = useState(0)
  const [open, setOpen] = useState<Newsflash | null>(null)
  const previous = useRef<Newsflash[]>([])
  const reported = useRef<Set<number>>(new Set())
  const bannerRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      const next = await getNewsflashes()
      setIndex(i => keepPosition(previous.current, next, i))
      previous.current = next
      setNewsflashes(next)
    } catch {
      // A failed check keeps what is showing; the next check tries again.
    }
  }, [])

  useEffect(() => {
    load()
    const timer = window.setInterval(load, POLL_MS)
    const onVisible = () => document.visibilityState === 'visible' && load()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [load])

  const current = newsflashes[Math.min(index, newsflashes.length - 1)]

  useEffect(() => {
    if (!current || reported.current.has(current.newsflashId)) return
    reported.current.add(current.newsflashId)
    postNewsflashAction(current.newsflashId, 'seen').catch(() => undefined)
  }, [current])

  useLayoutEffect(() => {
    const root = document.documentElement
    const el = bannerRef.current
    if (!el) {
      root.style.setProperty('--newsflash-height', '0px')
      return
    }
    const sync = () => root.style.setProperty('--newsflash-height', `${el.offsetHeight}px`)
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.setProperty('--newsflash-height', '0px')
    }
  }, [current])

  const remove = (id: number, action: 'dismiss' | 'confirm') => {
    const next = newsflashes.filter(n => n.newsflashId !== id)
    setIndex(i => keepPosition(newsflashes, next, i))
    previous.current = next
    setNewsflashes(next)
    setOpen(null)
    postNewsflashAction(id, action).catch(() => undefined)
  }

  useEffect(() => {
    if (!open || open.needsConfirm) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!current) return null

  const total = newsflashes.length
  const step = (by: number) => setIndex(i => (i + by + total) % total)

  return (
    <>
      <div ref={bannerRef} role="status" className={`newsflash newsflash--${current.severity}`}>
        <span className="newsflash__tag">{SEVERITY_TAG[current.severity]}</span>
        <span className="newsflash__text">
          <span className="newsflash__title">{current.title}</span>
          <span className="newsflash__summary"> · {current.summary}</span>
        </span>
        <span className="newsflash__actions">
          <button type="button" className="newsflash__link" onClick={() => setOpen(current)}>
            See details
          </button>
          {total > 1 && (
            <span className="newsflash__pager">
              <button type="button" aria-label="Previous message" onClick={() => step(-1)}>
                <ChevronLeft size={16} />
              </button>
              {index + 1} of {total}
              <button type="button" aria-label="Next message" onClick={() => step(1)}>
                <ChevronRight size={16} />
              </button>
            </span>
          )}
          {current.closable && (
            <button
              type="button"
              className="newsflash__icon"
              aria-label="Close message"
              onClick={() => remove(current.newsflashId, 'dismiss')}
            >
              <X size={16} />
            </button>
          )}
        </span>
      </div>

      {open && (
        <div
          className="newsflash-modal"
          onMouseDown={e => e.target === e.currentTarget && !open.needsConfirm && setOpen(null)}
        >
          <div
            className="newsflash-modal__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="newsflash-modal-title"
          >
            <div className="newsflash-modal__top">
              <div className="newsflash-modal__head">
                <span className="newsflash-modal__eyebrow">
                  <span className={`newsflash-modal__dot newsflash-modal__dot--${open.severity}`} />
                  {[SEVERITY_LABEL[open.severity], untilLabel(open.endsAt)].filter(Boolean).join(' · ')}
                </span>
                <h2 id="newsflash-modal-title" className="newsflash-modal__title">
                  {open.title}
                </h2>
              </div>
              {!open.needsConfirm && (
                <button type="button" className="newsflash-modal__x" aria-label="Close" onClick={() => setOpen(null)}>
                  <X size={18} />
                </button>
              )}
            </div>
            {open.bodyHtml ? (
              <div
                className="newsflash-modal__body"
                // Cleaned to headings, paragraphs, bold and lists by the console before it is stored.
                dangerouslySetInnerHTML={{ __html: open.bodyHtml }}
              />
            ) : (
              <p className="newsflash-modal__body">{open.summary}</p>
            )}
            <div className="newsflash-modal__foot">
              {!open.needsConfirm && (
                <button type="button" className="newsflash-modal__btn" onClick={() => setOpen(null)}>
                  Close
                </button>
              )}
              <button
                type="button"
                className="newsflash-modal__btn newsflash-modal__btn--primary"
                autoFocus
                onClick={() => remove(open.newsflashId, open.needsConfirm ? 'confirm' : 'dismiss')}
              >
                {open.needsConfirm ? 'Got it, we’ll do this' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default NewsflashBanner
